<template>
  <div ref="chartRef" class="chart-container"></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import * as echarts from 'echarts'

// 接收父组件传入的时间列表数据
const props = defineProps({
  timeList: {
    type: Array,
    default: () => []
  }
})

const chartRef = ref(null)
let chartInstance = null
let resizeObserver = null

// 从 timeList 中提取 x 轴数据和系列数据
const xAxisData = computed(() => props.timeList.map(item => item.hour))
const seriesData = computed(() => props.timeList.map(item => item.warningCount))

// 生成 ECharts 配置（使用计算属性获取最新数据）
const getChartOption = () => ({
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    formatter: '{c}人<br/>{b}点'
  },
  grid: {
    left: '3%',
    right: '3%',
    bottom: 10,
    top: 30,
    containLabel: true
  },
  xAxis: {
    type: 'category',
    data: xAxisData.value,
    boundaryGap: false,
    axisLine: { lineStyle: { color: '#fff' } },
    axisLabel: { fontSize: 12 }
  },
  yAxis: {
    type: 'value',
    name: '单位（人次）',
    nameTextStyle: { color: '#ffffff' },
    axisLabel: {
    color: '#ffffff'      // 文字颜色：白色
    },
    axisLine: {
      show: true,               // 确保显示轴线
      lineStyle: {
        color: '#3b82f6',          // 轴线颜色（白色）
        width: 2,               // 轴线粗细
        type: 'solid'           // 实线
      }
    },
    splitLine: { show: false },
    axisTick: { show: true }        // 可选：隐藏刻度线
  },
  series: [
    {
      name: '数值',
      type: 'line',
      data: seriesData.value,
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      lineStyle: {
        width: 2,
        color: '#3b82f6',
        shadowBlur: 10,
        shadowColor: 'rgba(59,130,246,0.3)'
      },
      areaStyle: {
        opacity: 0.1,
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: '#3b82f6' },
          { offset: 1, color: '#ffffff' }
        ])
      },
      itemStyle: {
        color: '#3b82f6',
        borderColor: '#fff',
        borderWidth: 2
      },
      label: {
        show: false,
        position: 'top',
        color: '#fff',
        fontSize: 12
      }
    }
  ]
})

// 更新图表配置
const updateChart = () => {
  if (chartInstance) {
    chartInstance.setOption(getChartOption(), true) // true 表示完全替换配置
  }
}

// 初始化图表
const initChart = () => {
  if (!chartRef.value) return
  chartInstance = echarts.init(chartRef.value)
  updateChart()
}

// 监听 timeList 变化，更新图表
watch(() => props.timeList, () => {
  updateChart()
}, { deep: true, immediate: true }) // immediate 确保初次有数据时也能触发

// 窗口尺寸自适应
const handleResize = () => {
  chartInstance?.resize()
}

// 使用 ResizeObserver 监听容器尺寸变化
const observeResize = () => {
  if (!chartRef.value) return
  resizeObserver = new ResizeObserver(() => {
    chartInstance?.resize()
  })
  resizeObserver.observe(chartRef.value)
}

onMounted(() => {
  initChart()
  window.addEventListener('resize', handleResize)
  observeResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  resizeObserver?.disconnect()
  chartInstance?.dispose()
})
</script>

<style scoped>
.chart-container {
  flex: 1;
  width: 100%;
  min-height: 0;
}
</style>